/**
 * Sparkline — animated SVG trend line that "draws" on mount (Apple-style).
 * Pure CSS stroke-dashoffset animation, no external deps.
 */
import React, { useEffect, useRef, useState } from 'react';

export default function Sparkline({
  data = [],
  width = 120,
  height = 36,
  color = '#10b981',
  strokeWidth = 2,
  fill = true,
  className = '',
}) {
  const pathRef = useRef(null);
  const [len, setLen] = useState(0);
  const [drawn, setDrawn] = useState(false);

  const min = Math.min(...data);
  const max = Math.max(...data);
  const span = max - min || 1;
  const stepX = data.length > 1 ? width / (data.length - 1) : width;
  const pts = data.map((v, i) => {
    const x = i * stepX;
    const y = height - ((v - min) / span) * (height - strokeWidth * 2) - strokeWidth;
    return [x, y];
  });
  const line = pts.map((p, i) => `${i === 0 ? 'M' : 'L'}${p[0].toFixed(1)},${p[1].toFixed(1)}`).join(' ');
  const area = `${line} L${width},${height} L0,${height} Z`;

  useEffect(() => {
    if (pathRef.current) {
      const l = pathRef.current.getTotalLength();
      setLen(l);
      requestAnimationFrame(() => requestAnimationFrame(() => setDrawn(true)));
    }
  }, [line]);

  const gid = `sg-${color.replace('#', '')}`;
  return (
    <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} className={className} preserveAspectRatio="none">
      <defs>
        <linearGradient id={gid} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.22" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
      {fill && (
        <path d={area} fill={`url(#${gid})`} opacity={drawn ? 1 : 0} style={{ transition: 'opacity 900ms ease 300ms' }} />
      )}
      <path
        ref={pathRef}
        d={line}
        fill="none"
        stroke={color}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeLinejoin="round"
        style={{
          strokeDasharray: len,
          strokeDashoffset: drawn ? 0 : len,
          transition: 'stroke-dashoffset 1100ms cubic-bezier(0.22,1,0.36,1)',
        }}
      />
    </svg>
  );
}
